import {
  NJButton,
  NJHeading,
  NJIconButton,
  NJInlineMessage,
  NJSelectItem,
  NJSelectRoot,
  NJSpinner,
  NJTag,
  NJText,
} from "@engie-group/fluid-design-system-react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import type { components } from "@/api/generated-types";
import { BackButton } from "@/components/BackButton";
import { DataTable } from "@/components/DataTable";
import { requiredLabel } from "@/components/form/validation";
import { useAdminUsers } from "./useAdminUsers";
import {
  useAddGroupMember,
  useAdminGroup,
  useRemoveGroupMember,
} from "./useAdminGroups";

type GroupMember = components["schemas"]["AdminUserIdentity"];

function MemberActionsCell({
  data,
  groupName,
}: ICellRendererParams<GroupMember> & { groupName?: string }) {
  const removeMember = useRemoveGroupMember();
  if (!data) return null;
  return (
    <NJIconButton
      icon="person_remove"
      variant="destructive"
      label={`Remove ${data.login} from the group`}
      isDisabled={removeMember.isPending}
      onClick={() =>
        groupName && removeMember.mutate({ groupName, userId: data.id })
      }
    />
  );
}

/** Admin group detail — members grid (remove via icon) + add member from a user picker.
 * Membership actions enforce the app-admin/agent-access mutual-exclusion rule server-side. */
export function AdminGroupDetailPage() {
  const { name } = useParams<{ name: string }>();
  const { data: group, isLoading, isError } = useAdminGroup(name);
  const { data: usersPage } = useAdminUsers({ limit: 100 });
  const addMember = useAddGroupMember();
  const [userToAdd, setUserToAdd] = useState("");

  const candidates = useMemo(() => {
    const memberIds = new Set(
      (group?.members ?? []).map((member) => member.id),
    );
    return (usersPage?.items ?? []).filter((user) => !memberIds.has(user.id));
  }, [group, usersPage]);

  const columnDefs = useMemo<ColDef<GroupMember>[]>(
    () => [
      { headerName: "Login", field: "login", flex: 1 },
      {
        headerName: "Name",
        valueGetter: (params) =>
          params.data
            ? `${params.data.firstName} ${params.data.lastName}`.trim()
            : "",
        flex: 1,
      },
      { headerName: "Email", field: "email", flex: 2 },
      {
        headerName: "Actions",
        cellRenderer: MemberActionsCell,
        cellRendererParams: { groupName: name },
        flex: 1,
        sortable: false,
      },
    ],
    [name],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <NJSpinner aria-label="Loading the group" />
      </div>
    );
  }

  if (isError || !group) {
    return (
      <div className="flex flex-col gap-4">
        <BackButton to="/admin/groups" label="Back to Groups" />
        <NJInlineMessage variant="error">
          Could not load this group.
        </NJInlineMessage>
      </div>
    );
  }

  const members = group.members ?? [];

  return (
    <div className="flex flex-col gap-6">
      <BackButton to="/admin/groups" label="Back to Groups" />

      <div className="flex flex-wrap items-center gap-3">
        <NJHeading as="h1" scale="lg">
          {group.name}
        </NJHeading>
        <NJTag label={group.type} variant="grey" size="sm" />
      </div>

      <section className="grid gap-4 border border-nj-neutral-subtle-default p-6 sm:grid-cols-2">
        <Field label="Members" value={String(group.membersCount)} />
        <Field label="Linked agent" value={group.linkedAgentSlug || "-"} />
        {group.description && (
          <div className="sm:col-span-2">
            <Field label="Description" value={group.description} />
          </div>
        )}
      </section>

      <section className="flex flex-col gap-3 border border-nj-neutral-subtle-default p-6">
        <NJHeading as="h2" scale="sm">
          Add a member
        </NJHeading>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full">
            <NJSelectRoot
              id="group-add-member"
              label={requiredLabel("User to add")}
              value={userToAdd}
              listNavigationLabel="Use the arrows and Enter to choose a user"
              buttonDefaultValueLabel="Choose a user"
              onChange={(value) => setUserToAdd(value ?? "")}
            >
              {candidates.map((user) => (
                <NJSelectItem key={user.id} value={user.id}>
                  {`${user.login} (${user.email})`}
                </NJSelectItem>
              ))}
            </NJSelectRoot>
          </div>
          <NJButton
            label="Add member"
            disabled={!userToAdd || addMember.isPending}
            onClick={() => {
              if (name) {
                addMember.mutate(
                  { groupName: name, userId: userToAdd },
                  { onSuccess: () => setUserToAdd("") },
                );
              }
            }}
          />
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <NJHeading as="h2" scale="sm">
          Members
        </NJHeading>
        {members.length > 0 ? (
          <DataTable<GroupMember> rowData={members} columnDefs={columnDefs} />
        ) : (
          <NJText
            as="p"
            scale="sm"
            className="text-nj-neutral-tertiary-default"
          >
            This group has no member yet.
          </NJText>
        )}
      </section>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <NJText
        as="span"
        scale="md"
        className="font-nj-bold text-nj-neutral-primary-default"
      >
        {label}
      </NJText>
      <NJText
        as="span"
        scale="xs"
        className="text-nj-neutral-secondary-default"
      >
        {value}
      </NJText>
    </div>
  );
}
